import { BaseModel, type RelationConfig } from './base';

// Enums

export enum IntegrationActionType {
  callback = 1,
  internalUrl = 2,
  externalUrl = 3,
}

export const IntegrationActionTypeLabels: Record<IntegrationActionType, string> = {
  [IntegrationActionType.callback]: 'integration.callback',
  [IntegrationActionType.internalUrl]: 'integration.internalUrl',
  [IntegrationActionType.externalUrl]: 'integration.externalUrl',
};

export enum IntegrationAuthenticateType {
  url = 'url',
  none = 'none',
}

export const IntegrationAuthenticateTypeLabels: Record<IntegrationAuthenticateType, string> = {
  [IntegrationAuthenticateType.url]: 'general.url',
  [IntegrationAuthenticateType.none]: 'general.none',
};

export enum IntegrationAuthType {
  none = 0,
  url = 1,
  page = 2,
}

export const IntegrationAuthTypeLabels: Record<IntegrationAuthType, string> = {
  [IntegrationAuthType.none]: 'general.none',
  [IntegrationAuthType.url]: 'general.url',
  [IntegrationAuthType.page]: 'general.page',
};

export enum IntegrationStatus {
  disconnected = 'disconnected',
  connected = 'connected',
  setupIncomplete = 'setupIncomplete',
  authError = 'authError',
}

export const IntegrationStatusLabels: Record<IntegrationStatus, string> = {
  [IntegrationStatus.disconnected]: 'integration.disconnected',
  [IntegrationStatus.connected]: 'integration.connected',
  [IntegrationStatus.setupIncomplete]: 'integration.setupIncomplete',
  [IntegrationStatus.authError]: 'integration.needsReconnecting',
};

// Helper Classes

export class IntegrationOption extends BaseModel {}

export class IntegrationAuthenticate extends BaseModel {
  type!: IntegrationAuthenticateType;
  url!: string | null;

  get isTypeUrl(): boolean {
    return this.type === IntegrationAuthenticateType.url;
  }

  get isTypeNone(): boolean {
    return this.type === IntegrationAuthenticateType.none;
  }
}

export class IntegrationPaymentOrder extends BaseModel {
  order!: Integration[];

  static override relations(): RelationConfig {
    return {
      order: { type: Integration },
    };
  }
}

export class IntegrationLabel extends BaseModel {
  type!: string;
  label!: string;
  value!: string;

  get isText(): boolean {
    return this.type === 'text';
  }

  get isUrl(): boolean {
    return this.type === 'url';
  }
}

export class IntegrationAction extends BaseModel {
  type!: IntegrationActionType;
  code!: string;
  label!: string;

  get isCallback(): boolean {
    return this.type === IntegrationActionType.callback;
  }

  get isInternalUrl(): boolean {
    return this.type === IntegrationActionType.internalUrl;
  }

  get isExternalUrl(): boolean {
    return this.type === IntegrationActionType.externalUrl;
  }
}

// Main Model

export class Integration extends BaseModel {
  guid!: string;
  name!: string;
  status!: IntegrationStatus;
  authType!: IntegrationAuthType;
  platforms!: string[];

  override objectID(): string | null {
    return this.guid ?? null;
  }

  get isPlatformWeb(): boolean {
    return this.platforms?.includes('web') ?? false;
  }

  get isPlatformApple(): boolean {
    return this.platforms?.includes('ios') ?? false;
  }

  get isPlatformAndroid(): boolean {
    return this.platforms?.includes('android') ?? false;
  }

  get isSquare(): boolean {
    return this.guid === 'b0bca8b297674792a6f4489d272b8230';
  }

  get isStripe(): boolean {
    return this.guid === '29bdebaccd9e452fac2be8963d3daba7';
  }

  get isPayPal(): boolean {
    return this.guid === '795fc26230c941cc89bc3c6eaae65e30';
  }

  get isStatusDisconnected(): boolean { return this.status === IntegrationStatus.disconnected; }
  get isStatusConnected(): boolean { return this.status === IntegrationStatus.connected; }
  get isStatusSetupIncomplete(): boolean { return this.status === IntegrationStatus.setupIncomplete; }
  get isStatusAuthError(): boolean { return this.status === IntegrationStatus.authError; }

  get isAuthTypeNone(): boolean { return this.authType === IntegrationAuthType.none; }
  get isAuthTypeUrl(): boolean { return this.authType === IntegrationAuthType.url; }
  get isAuthTypePage(): boolean { return this.authType === IntegrationAuthType.page; }
}
